import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as eventForm from "../../forms/createEvent";
import { LibItem, UserShortInfo } from "../../types";
import ManShortSelector from "../../components/ui/shortItemSelect";
import {
  libBackend,
  backend,
  EventCreationData,
  eventBackend,
} from "../../services";
import { actions, RootState } from "../../state/reducer";
import { toast } from "sonner";
import DateInput from "../../components/ui/dateInput";
import { defaultCurrencyId } from "../../constants";

interface ModalLoaderProps {
  message: string;
  fixedCustomerId?: number;
  defaultCustomerId?: number;
  defaultResponsibleId?: number;
  onSave: () => void;
  closeModal: () => void;
}

const CreateEventModal: React.FC<ModalLoaderProps> = ({
  fixedCustomerId,
  defaultCustomerId,
  defaultResponsibleId,
  onSave,
  closeModal,
}) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const groupList = State.groupsToSelect;
  const rights = State.rights;
  const [selectedGroup, setSelectedGroup] = useState<number>(
    rights.group_ids[0]
  );
  const [isSelectionsLoaded, setSelectionsLoaded] = useState(false);
  const [responceUsersToSelect, setUsersToSelect] = useState<UserShortInfo[]>(
    []
  );
  const [customersToSelect, setCustomersToSelect] = useState<UserShortInfo[]>(
    []
  );
  const [isWithTransfer, setIsWithTransfer] = useState<boolean>();
  const [selectedUserId, setSelectedUser] = useState<number>();
  const [selectedCustomerId, setSelectedCustomer] = useState<
    number | undefined
  >(fixedCustomerId);
  const [eventTypeList, setEventTypeList] = useState<LibItem[]>([]);
  const [comment, setComment] = useState<string | undefined>();
  const calendarRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    date_end: "",
    customer_id: fixedCustomerId || undefined,
    type_id: 0,
    amount: 0,
    currency: defaultCurrencyId
  });

  const session = State.session;

  const closeWithSave = async () => {
    await saveAction();
    onSave(); // Передаем новое фото в callback
    closeModal(); // Закрываем модальное окно
  };

  useEffect(() => {
    backend.getAllUsersShorts({ group_id: selectedGroup }).then((res) => {
      setUsersToSelect(res.users);
    });
  }, [selectedGroup]);

  useEffect(() => {
    backend.getAllCustomersShorts({ user_id: session?.id }).then((res) => {
      setCustomersToSelect(
        res.customers.filter((item) =>
          fixedCustomerId ? fixedCustomerId === item.id : true
        )
      );
    });
    libBackend.getEventTypeList().then((res) => {
      const types =
        rights.rang <= 2
          ? res.kinds
          : res.kinds.filter((item) => {
              return rights.permitted_event_types.indexOf(item.id) > -1;
            });
      setEventTypeList(types);
      setSelectionsLoaded(true);
    });
    setSelectedUser(session?.id);
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveAction = async () => {
    console.log("Save action called");
    return new Promise((resolve) => {
      if (formData.type_id === 0) {
        toast("Выберите тип события!");
        return;
      }
      if (!selectedUserId || !selectedCustomerId) {
        toast("Клиент и ответственный должны быть указаны");
        return;
      }
      const now = new Date();
      const date_end = new Date(formData.date_end);
      if (!formData.date_end || date_end < now) {
        toast("Дата выполнения должна быть больше текущей");
        return;
      }
      const dataToSave: EventCreationData = {
        ...formData,
        date_end: formData.date_end
          ? new Date(formData.date_end).getTime()
          : undefined,
        responsible_id: selectedUserId,
        customer_id: selectedCustomerId,
        comment,
        transfer_customer: isWithTransfer || undefined
      };
      eventBackend
        .createEvent(dataToSave)
        .then((res) => {
          if (res.newCustomerData) {
            dispatch(actions.customerDisplayData(res.newCustomerData));
            toast("Связанный клиент передан другому пользователю")
          } else {
            toast(res.success ? "Событие создано" : "Не удалось создать событие");
          }
          resolve(true);
        })
        .catch(() => {
          toast("Ошибка создания события");
          resolve(false);
        });
      console.log("To save:", dataToSave);
    });
  };

  const handleDateSelect = (dateTime: string) => {
    setFormData((prev) => ({ ...prev, ["date_end"]: dateTime }));
  };

  return (
    <div className="creationForm">
      <div className="modalHeading">
        <h3>Создание события</h3>
      </div>
      <form className="columnCenter">
        {/* Поле: Название */}
        {isSelectionsLoaded && (
          <div className="formField formRow">
            <label htmlFor="type">Категория события</label>
            <select
              id="selectType"
              name="type_id"
              value={formData.type_id}
              onChange={handleInputChange}
            >
              <option value={0} disabled>
                Выберите тип
              </option>
              {eventTypeList.map((item, index) => {
                return (
                  <option key={`evCreateType${index}`} value={item.id}>
                    {item.name}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {/* Поле: Дедлайн */}
        <div className="formField formRow dateInputSingle">
          <label htmlFor="date_end">Срок:</label>
          <DateInput
            value={formData.date_end}
            onSelect={handleDateSelect}
            onlyFuture={true}
          />
        </div>

        {/* Поле: Значение */}
        {(Number(formData.type_id) === 16 ||
          Number(formData.type_id) === 17) && (
          <div className="formField formRow">
            <label htmlFor="value">Сумма, $</label>
            <input
              type="amount"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
            />
          </div>
        )}
        {!fixedCustomerId && (
          <div className="formField formRow">
            <label>Клиент</label>
            <ManShortSelector
              items={customersToSelect}
              defaultSelectedId={defaultCustomerId}
              onChangeCallback={(id) => setSelectedCustomer(id)}
            />
          </div>
        )}
        <div className="flex formRow">
          <div className="fieldName">Группа</div>
          <div className="fieldInput flex">
            <select
              id="group_id"
              name="group_id"
              value={selectedGroup}
              onChange={(event) => {
                const group = Number(event.target.value);
                backend.getAllUsersShorts({ group_id: group }).then((res) => {
                  setUsersToSelect(res.users);
                });
                setSelectedGroup(group);
              }}
            >
              {rights.rang < 3 && <option value="0">Любая</option>}
              {groupList.map((item, index) => {
                return (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="formField formRow">
          <label>Ответственный</label>
          <ManShortSelector
            items={responceUsersToSelect}
            defaultSelectedId={selectedUserId}
            onChangeCallback={(id) => setSelectedUser(id)}
          />
        </div>
        <div className="flex formRow">
          <div className="fieldName flex gap12">
            <input
              className="checkBoxInput"
              type="checkbox"
              checked={isWithTransfer || false}
              onChange={(e) => setIsWithTransfer(e.target.checked)}
            />
            <span>Передать клиента новому ответственному</span>
          </div>
        </div>
        <div className="formField formRow commentRow">
          <label htmlFor="description">Комментарий</label>
          <textarea
            id="description"
            name="description"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </div>
        {/* Кнопки действий */}
        <div className="flex between btnRow">
          <button
            className="filterButton"
            type="button"
            onClick={closeWithSave}
          >
            Сохранить
          </button>
          <button className="cancelButton" type="button" onClick={closeModal}>
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEventModal;
